<script setup lang="ts">
/**
 * Renders the dialogs asked for through `useNxConfirm()` and `useNxPrompt()`,
 * one at a time in order. Mount it once in the app shell (next to
 * `NxToastHost`); with nested hosts only the most recently mounted one shows
 * the queue. Without any host the two APIs fall back to the browser's own
 * `confirm()` / `prompt()`.
 */
import { ref, watch } from 'vue';
import NxConfirmDialog from './NxConfirmDialog.vue';
import NxPromptDialog from './NxPromptDialog.vue';
import {
  currentDialogRequest,
  settleDialog,
  useNxConfirmHostPresence,
  type NxDialogRequest,
} from './dialogQueue';
import type { NxConfirmChoice } from './types';

const isTopHost = useNxConfirmHostPresence();

/** The request this host shows; kept while the dialog fades out. */
const shown = ref<NxDialogRequest | null>(null);
const open = ref(false);
const promptValue = ref('');

watch(
  [currentDialogRequest, isTopHost],
  ([request, top]) => {
    if (!top || !request || shown.value?.id === request.id) return;
    shown.value = request;
    if (request.kind === 'prompt') promptValue.value = request.value;
    open.value = true;
  },
  { immediate: true }
);

function finishConfirm(choice: NxConfirmChoice) {
  const request = shown.value;
  if (!request || request.kind !== 'confirm') return;
  open.value = false;
  settleDialog(request.id);
  request.resolve(choice);
}

function finishPrompt(value: string | null) {
  const request = shown.value;
  if (!request || request.kind !== 'prompt') return;
  open.value = false;
  settleDialog(request.id);
  request.resolve(value);
}
</script>

<template>
  <template v-if="isTopHost && shown">
    <NxConfirmDialog
      v-if="shown.kind === 'confirm'"
      :key="shown.id"
      v-model:open="open"
      :title="shown.title"
      :message="shown.message"
      :confirm-label="shown.confirmLabel"
      :cancel-label="shown.cancelLabel"
      :alternative-label="shown.alternativeLabel"
      :tone="shown.tone"
      @confirm="finishConfirm('confirm')"
      @alternative="finishConfirm('alternative')"
      @cancel="finishConfirm('cancel')"
    />
    <NxPromptDialog
      v-else
      :key="shown.id"
      v-model:open="open"
      v-model="promptValue"
      :title="shown.title"
      :message="shown.message"
      :label="shown.label"
      :placeholder="shown.placeholder"
      :confirm-label="shown.confirmLabel"
      :cancel-label="shown.cancelLabel"
      :required="shown.required"
      :validate="shown.validate"
      @submit="finishPrompt"
      @cancel="finishPrompt(null)"
    />
  </template>
</template>
